
import { eLynxObjectTypeEnum } from "../../common/enums/eLynxObjectType";
import { IeLynxObject } from "../../common/models/eLynxObject";
import { getObjectTypeName } from "../../common/utilities/GetObjectTypeName";
import restService from "../rest-service/RestService";
import { ICriteriaModel, IGroupModel, ISelectableObject, ISystemBalanceObjectResultModel } from "./Models";



interface IGroupRestQueryParams {
    groupID?: number,
    objectType?: eLynxObjectTypeEnum,
    includeChildOrgs?: boolean,
    excludeDynamicDevices?: boolean,
    includeTanks?: boolean,
    excludeHidden?: boolean

}
const GroupDataService = {
    
    getGroupPath: async function(groupID: number, signal?: AbortSignal): Promise<IGroupModel> {
        const response = await restService.get<IGroupModel>(`/groups/${groupID}/path`, signal);
        return response.data;
    },
    getGroupWithChildGroups: async function(groupID: number, signal?: AbortSignal): Promise<IGroupModel[]> {
        const response = await restService.get<IGroupModel[]>(`/groups/${groupID}/childgroups`, signal);
        return response.data;

    },
    addGroup: async function(contextGroupID: number, group: IGroupModel): Promise<IGroupModel> {
        const response = await restService.post<IGroupModel, IGroupModel>(`/groups/${contextGroupID ?? group.id}`, group);
        return response.data;
    },
    updateGroup: async function(contextGroupID: number, group: IGroupModel): Promise<IGroupModel> {
        const response = await restService.put<IGroupModel, IGroupModel>(`/groups/${contextGroupID ?? group.id}`, group);
        return response.data;
    },
    updateGroups: async function(contextGroupID: number, groups: IGroupModel[]): Promise<IGroupModel[]> {
        const response = await restService.put<IGroupModel[], IGroupModel[]>(`/groups/${contextGroupID}/list`, groups);
        return response.data;
    },
    deleteGroup: async function(groupID: number): Promise<void> {
        await restService.delete(`/groups/${groupID}`);
    },
    getGroupContents: async function(groupID: number, 
            objectType?: eLynxObjectTypeEnum, 
            includeChildOrgs?: boolean, 
            excludeDynamicDevices?: boolean, 
            includeTanks?: boolean,
            excludeHidden?: boolean,
            signal?: AbortSignal): Promise<ISelectableObject[]> {


        const queryParams: IGroupRestQueryParams = {
            objectType: objectType,
            includeChildOrgs: includeChildOrgs,
            excludeDynamicDevices: excludeDynamicDevices,
            includeTanks: includeTanks,
            excludeHidden: excludeHidden
        };
        
        const response = await restService.get<ISelectableObject[], IGroupRestQueryParams>(`/groups/${groupID}/contentconfiguration`, signal, queryParams);
        
        return response.data;
    },

    updateGroupContents: async function(groupID: number, contents: ISelectableObject[]): Promise<IGroupModel> {
        const queryParams: IGroupRestQueryParams = { groupID: groupID };
        const response = await restService.put<ISelectableObject[], IGroupRestQueryParams, IGroupModel>(`/groups/${groupID}/contentconfiguration`, contents, undefined, queryParams);
        return response.data;
    },
    getGroupManualEntryTats: async function(groupID: number, signal?: AbortSignal): Promise<ISelectableObject[]> {

        const response = await restService.get<ISelectableObject[]>(`/groups/${groupID}/manualentry/configuration`, signal);
        return response.data;
    },
    updateGroupManualEntryConfiguration: async function (groupID: number, tatList: ISelectableObject[]): Promise<void> {

        const queryParams: IGroupRestQueryParams = { groupID: groupID };
        const response = await restService.put<ISelectableObject[], IGroupRestQueryParams, void>(`/groups/${groupID}/manualentry/configuration`, tatList, undefined, queryParams);
        return response.data;
    },
    getSystemBalanceObjectConfiguration: async function (groupID: number, signal?: AbortSignal): Promise<ISystemBalanceObjectResultModel> {
        const response = await restService.get<ISystemBalanceObjectResultModel>(`/groups/${groupID}/systembalance/configuration`, signal);
        return response.data;
    },

    saveSystemBalanceObjectConfiguration: async function(groupID: number, systemBalanceModel: ISystemBalanceObjectResultModel): Promise<ISystemBalanceObjectResultModel> {
        const response = await restService.put<ISystemBalanceObjectResultModel, ISystemBalanceObjectResultModel>(`/groups/${groupID}/systembalance/configuration`, systemBalanceModel);
        return response.data; 
    },
    getMembershipData: async function(groupID: number, eLynxObject: IeLynxObject, signal?: AbortSignal): Promise<IGroupModel[]> {

        const response = await restService.get<IGroupModel[]>(`/groups/${groupID}/${getObjectTypeName(eLynxObject.objectType, true).toLowerCase()}/${eLynxObject.id}/staticgroupmembership`, signal);
        return response.data;
    },
    cloneGroupMembership: async function(sourceDeviceID: number, destinationDeviceID: number, groupIDs: number[] ): Promise<number> {

        const response = await restService.put<number[], number>(`/groups/devices/${sourceDeviceID}/${destinationDeviceID}/clone`, groupIDs);
        return response.data; 
    },
    
    getDynamicGroupCriteria: async function(groupID: number , signal?: AbortSignal): Promise<ICriteriaModel> {

        const response = await restService.get<ICriteriaModel>(`/groups/${groupID}/criteria`, signal);
        return response.data;
    },

    saveDynamicGroupCriteria: async function(groupID: number, criteria: ICriteriaModel): Promise<ICriteriaModel> {
        const response = await restService.put<ICriteriaModel, ICriteriaModel>(`/groups/${groupID}/criteria`, criteria);
        return response.data; 
    },
    getTagAssociationTypes: async function (groupID: number, signal?: AbortSignal): Promise<ISelectableObject[]> {

        const response = await restService.get<ISelectableObject[]>(`/groups/${groupID}/tagassociationtypes`, signal);
        return response.data;
    },
    getTagAssociationTypesInUse: async function(groupID: number, signal?: AbortSignal): Promise<ISelectableObject[]> {

        const response = await restService.get<ISelectableObject[]>(`/groups/${groupID}/tagassociationtypes/inuse`, signal);
        return response.data;
    },
    getTankBatteries: async function(groupID: number, signal?: AbortSignal): Promise<ISelectableObject[]> {

        const response = await restService.get<ISelectableObject[]>(`/groups/${groupID}/tankbatteries`, signal);
        return response.data;
    },

};

export default GroupDataService;